import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { JobListing, SkillMatch } from 'job-bank-data-access';
import { assetBaseUrl } from '../asset-base-url';
import { initialsFor, formatSalary, formatPostedDate, formatStartDate } from '../listing-format';
import { DirectApplyPage } from './DirectApplyPage';
import { ResumeUploadPage } from './ResumeUploadPage';
import './JobDetailsPage.css';

export interface JobDetailsPageProps {
  listing: JobListing;
  matchedSkills: SkillMatch[];
  onBack: () => void;
}

type ApplyStep = 'questions' | 'resume';

// Geometry only — every visual property is set in JobDetailsPage.css so the
// stroke can inherit the row's colour modifier via currentColor.
function SkillMatchedIcon() {
  return (
    <svg viewBox="0 0 24 24" className="job-details__skill-glyph" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function SkillUnmatchedIcon() {
  return (
    <svg viewBox="0 0 24 24" className="job-details__skill-glyph" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

export function JobDetailsPage({ listing, matchedSkills, onBack }: JobDetailsPageProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  const backRef = useRef<HTMLButtonElement>(null);
  const applyRef = useRef<HTMLButtonElement>(null);
  const isFirstRender = useRef(true);
  const [applyStep, setApplyStep] = useState<ApplyStep | null>(null);

  // Mount: focus the back-to-matches link (existing JobMatchesPage -> JobDetailsPage
  // convention). Returning all the way out of the apply flow (applyStep -> null)
  // is a different transition -- spec 009 requires focus land on the apply
  // button the citizen used to get there, not the top-of-page back link.
  // JobDetailsPage is never unmounted by that round trip, so this can't be
  // two separate mount effects; a ref distinguishes first render from a
  // later return. Stepping between 'questions' and 'resume' (spec 011) is
  // each page's own concern -- DirectApplyPage and ResumeUploadPage both
  // focus their own heading on mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      backRef.current?.focus();
      return;
    }
    if (applyStep === null) applyRef.current?.focus();
  }, [applyStep]);

  if (applyStep === 'questions') {
    return (
      <DirectApplyPage
        listing={listing}
        onContinue={() => setApplyStep('resume')}
        onBack={() => setApplyStep(null)}
      />
    );
  }

  if (applyStep === 'resume') {
    return (
      <ResumeUploadPage listing={listing} onBack={() => setApplyStep('questions')} onBackToMatches={onBack} />
    );
  }

  const title = locale === 'fr' ? listing.title.fr : listing.title.en;
  const description = locale === 'fr' ? listing.description.fr : listing.description.en;
  const salary = `${formatSalary(listing.salaryMin, listing.salaryMax, locale)} ${t('jobDetails.salaryPerYearSuffix')}`;
  const posted = formatPostedDate(listing.postedDaysAgo, t, 'jobDetails');
  const startDate = formatStartDate(listing.startDate, locale, t, 'jobDetails');
  const initials = initialsFor(listing.employer);
  const responsibilities = listing.responsibilities.map((r) => (locale === 'fr' ? r.fr : r.en));

  return (
    <div className="job-details">
      <button ref={backRef} type="button" className="job-details__back" onClick={onBack}>
        &#8249; {t('jobDetails.back')}
      </button>

      <section className="job-details__heading">
        <h1 className="job-details__h1">{title}</h1>
        <div aria-hidden="true" className="job-details__accent-bar" />
      </section>

      <div className="job-details__card">
        <div className="job-details__header">
          <div className="job-details__avatar" aria-hidden="true">{initials}</div>
          <div className="job-details__header-body">
            <p className="job-details__employer">{listing.employer}</p>
            <p className="job-details__location">{listing.city}, {listing.province}</p>
            <p className="job-details__meta">{posted}</p>
          </div>
        </div>

        <div className="job-details__section">
          <h2 className="job-details__section-title">{t('jobDetails.sections.aboutRole')}</h2>
          <p className="job-details__body">{description}</p>
        </div>

        <div className="job-details__section">
          <h2 className="job-details__section-title">{t('jobDetails.sections.responsibilities')}</h2>
          <ul className="job-details__bullet-list">
            {responsibilities.map((item, i) => (
              <li key={i} className="job-details__bullet-item">{item}</li>
            ))}
          </ul>
        </div>

        <div className="job-details__section">
          <h2 className="job-details__section-title">{t('jobDetails.sections.whyMatch')}</h2>
          <p className="job-details__placeholder">{t('jobDetails.placeholders.whyMatch')}</p>
        </div>

        <div className="job-details__section">
          <h2 className="job-details__section-title">{t('jobDetails.sections.skillsChecklist')}</h2>
          <ul className="job-details__skills-list">
            {listing.skillsChecklist.map((skill) => {
              const match = matchedSkills.find((m) => m.skillId === skill.id);
              const label = locale === 'fr' ? skill.fr : skill.en;
              return (
                <li key={skill.id} className="job-details__skill-item">
                  <span
                    className={`job-details__skill-icon job-details__skill-icon--${
                      match ? 'matched' : 'unmatched'
                    }`}
                  >
                    {match ? <SkillMatchedIcon /> : <SkillUnmatchedIcon />}
                  </span>
                  <span className="job-details__skill-text">
                    <span className="job-details__skill-label">{label}</span>
                    {match && (
                      <span className="job-details__skill-match">
                        {t('jobDetails.skillsChecklist.matched', { term: match.citizenTerm })}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="job-details__section">
          <h2 className="job-details__section-title">{t('jobDetails.sections.details')}</h2>
          <div className="job-details__chips">
            <scds-badge variant="pill" tone="neutral" label={listing.workArrangement} />
            <scds-badge variant="pill" tone="neutral" label={salary} />
            <scds-badge variant="pill" tone="neutral" label={`${t('jobDetails.startDate')}: ${startDate}`} />
            {listing.benefits.map((b, i) => (
              <scds-badge key={i} variant="pill" tone="neutral" label={locale === 'fr' ? b.fr : b.en} />
            ))}
          </div>
        </div>

        <div className="job-details__cta-row">
          <button
            ref={applyRef}
            type="button"
            className="job-details__cta-primary"
            onClick={() => setApplyStep('questions')}
          >
            {t('jobDetails.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
